import { api } from '@services/api';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';
import { foInitialSetupUpdateStep } from '@store/slices/front-office/fo-initial-setup.slice';
import { APIInterface } from '@modules/front-office/interface/reports.interface';
import {
  AmenitiesResponse,
  updateRoomAmenitiesPayload,
} from '@modules/front-office/interface/fo-room-config-api-interface';

export const roomConfigApi = api
  .enhanceEndpoints({ addTagTypes: ['RoomConfig', 'RoomAmenities'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getRoomsData: builder.query({
        query: () => 'rooms',
        providesTags: ['RoomConfig'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            if (data) {
              dispatch(
                foInitialSetupUpdateStep({
                  key: 'rooms',
                  state:
                    data?.data?.rooms?.reduce(
                      (acc, item) => acc + (item?.room?.numberOfRooms || 0),
                      0,
                    ) || 0,
                }),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
      }),
      getRoomAmenities: builder.query({
        query: () => 'lists/room-amenities',
        forceRefetch: () => true,
        providesTags: ['RoomConfig', 'RoomAmenities'],
        transformResponse: (response: APIInterface<AmenitiesResponse>) => {
          return response.data;
        },
      }),
      getRoomData: builder.query({
        query: (params) => `rooms/${params}`,
        providesTags: ['RoomConfig'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
      }),
      getRoomNumbersOfRoom: builder.query({
        query: (params) => `rooms/${params}/roomNumbers`,
        providesTags: ['RoomConfig'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
      }),
      updateRoomAmenities: builder.mutation({
        query: ({ roomID, ...body }: updateRoomAmenitiesPayload) => ({
          url: `rooms/${roomID}/amenities`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['RoomAmenities'],
      }),
      createRooms: builder.mutation({
        query: (payload) => ({
          url: 'rooms',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['RoomConfig'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateRooms: builder.mutation({
        query: (payload) => ({
          url: 'rooms',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['RoomConfig'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
    }),
  });

export const {
  useGetRoomAmenitiesQuery,
  useGetRoomsDataQuery,
  useGetRoomNumbersOfRoomQuery,
  useGetRoomDataQuery,
  useLazyGetRoomAmenitiesQuery,
  useLazyGetRoomDataQuery,
  useLazyGetRoomNumbersOfRoomQuery,
  useLazyGetRoomsDataQuery,
  useCreateRoomsMutation,
  useUpdateRoomAmenitiesMutation,
  useUpdateRoomsMutation,
} = roomConfigApi;
